// src/services/invoiceService.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';
import {
  createService,
  createNestedCollection
} from './factory';

// Create the base invoice service using the factory
const invoiceServiceBase = createService('Invoice', '/finances/invoices', {
  nestedCollections: [
    createNestedCollection('payment')
  ],
  customEndpoints: {
    // Send an invoice to the client
    sendInvoice: async (invoiceId, emailData) => {
      return api.post(`/finances/invoices/${invoiceId}/send`, emailData);
    },
    
    // Send a reminder for an invoice
    sendReminder: async (invoiceId, reminderData) => {
      return api.post(`/finances/invoices/${invoiceId}/reminder`, reminderData);
    },
    
    // Approve an invoice
    approveInvoice: async (invoiceId, notes) => {
      return api.post(`/finances/invoices/${invoiceId}/approve`, { notes });
    },
    
    // Cancel an invoice
    cancelInvoice: async (invoiceId, reason) => {
      return api.post(`/finances/invoices/${invoiceId}/cancel`, { reason });
    },
    
    // Generate a PDF for an invoice
    generateInvoicePdf: async (invoiceId) => {
      return api.get(`/finances/invoices/${invoiceId}/pdf`);
    },
    
    // Get client invoice statistics
    getClientInvoiceStats: async (clientId) => {
      return api.get(`/finances/stats/client/${clientId}`);
    },
    
    // Get payment analytics
    getPaymentAnalytics: async (params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/finances/analytics/payments${queryString}`);
    },
    
    // Find overdue invoices
    findOverdueInvoices: async () => {
      return api.get('/finances/invoices/overdue');
    },
    
    // Create a payment link for an invoice
    createPaymentLink: async (invoiceId) => {
      return api.post(`/finances/invoices/${invoiceId}/payment-link`);
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getInvoices = async (params = {}) => {
  return invoiceServiceBase.getAll(params);
};

export const getInvoice = async (invoiceId) => {
  return invoiceServiceBase.getById(invoiceId);
};

export const createInvoice = async (invoiceData) => {
  return invoiceServiceBase.create(invoiceData);
};

export const updateInvoice = async (invoiceId, invoiceData) => {
  return invoiceServiceBase.update(invoiceId, invoiceData);
};

export const deleteInvoice = async (invoiceId) => {
  return invoiceServiceBase.delete(invoiceId);
};

// Add a payment to an invoice
export const addPayment = async (invoiceId, paymentData) => {
  return invoiceServiceBase.addPayment(invoiceId, paymentData);
};

// Send an invoice to the client
export const sendInvoice = async (invoiceId, emailData) => {
  return invoiceServiceBase.sendInvoice(invoiceId, emailData);
};

// Send a reminder for an invoice
export const sendReminder = async (invoiceId, reminderData) => {
  return invoiceServiceBase.sendReminder(invoiceId, reminderData);
};

// Approve an invoice
export const approveInvoice = async (invoiceId, notes) => {
  return invoiceServiceBase.approveInvoice(invoiceId, notes);
};

// Cancel an invoice
export const cancelInvoice = async (invoiceId, reason) => {
  return invoiceServiceBase.cancelInvoice(invoiceId, reason);
};

// Generate a PDF for an invoice
export const generateInvoicePdf = async (invoiceId) => {
  return invoiceServiceBase.generateInvoicePdf(invoiceId);
};

// Get client invoice statistics
export const getClientInvoiceStats = async (clientId) => {
  return invoiceServiceBase.getClientInvoiceStats(clientId);
};

// Get payment analytics
export const getPaymentAnalytics = async (params = {}) => {
  return invoiceServiceBase.getPaymentAnalytics(params);
};

// Find overdue invoices
export const findOverdueInvoices = async () => {
  return invoiceServiceBase.findOverdueInvoices();
};

// Create a payment link for an invoice
export const createPaymentLink = async (invoiceId) => {
  return invoiceServiceBase.createPaymentLink(invoiceId);
};